import React, { useEffect, useState } from 'react';
import {
    Box, Button, Modal, Table, TableBody, TableContainer,
    TableHead, TableRow, Paper, TablePagination,
    Typography,
    TextField,
    InputAdornment,
    CircularProgress,
    Menu,
    MenuItem, IconButton, Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from 'react-toastify';
import Papa from "papaparse";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getPresentAbsent } from '../../services/reportServices/mentorReportServices/AttendanceReportServices';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#7e7e7e",
        color: theme.palette.common.white,
        textTransform: "capitalize",
        fontSize: 16,
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd),&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

function highlightText(text, search) {
    if (!search) return text;
    const parts = text.split(new RegExp(`(${search})`, 'gi'));
    return parts.map((part, index) =>
        part.toLowerCase() === search.toLowerCase() ? <span key={index} style={{ backgroundColor: 'yellow' }}>{part}</span> : part
    );
}

function AttendanceDetailsModal({ onClose, open, selected }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [search, setSearch] = useState("");
    const [row, setRow] = useState([]);
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    useEffect(() => {
        const fetchCOEData = async () => {
            try {
                setLoading(true);
                if (selected.coe && selected.lab && selected.date) {
                    const response = await getPresentAbsent(selected.coe, selected.lab, selected.date, selected.attendanceId);
                    setRow(response?.data?.data || { presentLearners: [], absentLearners: [] });
                }
            } catch (error) {
                toast.error(error?.response?.data?.message || error?.message || "Error fetching COE data");
            } finally {
                setLoading(false);
            }
        };
        fetchCOEData();
    }, [selected]);

    const allLearners = selected.action === "Present" ? (row[0]?.presentLearners || []) : (row[0]?.absentLearners || []);
    const filteredLearners = allLearners.filter((learner) =>
        learner.name.toLowerCase().includes(search.toLowerCase()) ||
        learner.email.toLowerCase().includes(search.toLowerCase()) ||
        learner.mobile.includes(search)
    );

    const paginatedModules = filteredLearners.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDownloadClick = (event) => setAnchorEl(event.currentTarget);
    const handleCloseMenu = () => setAnchorEl(null);

    const handleDownload = (type) => {
        handleCloseMenu();

        if (!filteredLearners.length) {
            toast.warning("No data to export!");
            return;
        }

        const formatted = filteredLearners.map((r) => ({
            "Roll No": r.roll_num || "-",
            Name: r.name || "-",
            "Institution Email": r.institution_email || "-",
            "Personal Gmail": r.email || "-",
            Mobile: r.mobile || "-",
            "In Time": r.inDateTime ? r.inDateTime.split(" ").slice(1).join(" ") : "-",
        }));

        if (type === "csv") {
            const csv = Papa.unparse(formatted);
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "Mentor_Attendance_Details.csv";
            link.click();
        }

        if (type === "pdf") {
            const doc = new jsPDF();
            autoTable(doc, {
                startY: 20,
                head: [["Roll No", "Name", "Institution Email", "Personal Gmail", "Mobile", "In Time"]],
                body: formatted.map((r) => Object.values(r)),
                theme: "grid",
                headStyles: {
                    fillColor: [126, 126, 126],
                    halign: "center",
                    valign: "middle"
                },
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                    overflow: "linebreak",
                    halign: "center",
                    valign: "middle",
                    lineColor: [200, 200, 200],
                    lineWidth: 0.1,
                },
                didDrawPage: () => {
                    doc.setFontSize(12);
                    doc.text(`Attendance Details - ${selected.action === "Present" ? "Attended" : "Not Attended"}`, 14, 15);
                },
            });

            doc.save("Mentor_Attendance_Details.pdf");
        }
    };

    const modalTitle =
        selected?.action === "Present"
            ? "Attended Students"
            : selected?.action === "Absent"
            ? "Not Attended Students"
            : "Attendance Details";

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{ outline: "none !important", position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: { xs: "95%", sm: "90%", md: "85%" }, maxWidth: '95vw', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, maxHeight: "90vh !important", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" pb={2}>
                    <Box>
                        <Typography
                            className="dashboard-header-title"
                            sx={{ whiteSpace: "nowrap" }}
                        >
                            {modalTitle}
                        </Typography>

                        {!loading && (
                            <Typography variant="body2" color="text.secondary" mt={0.5}>
                                Total: <strong>{filteredLearners.length}</strong>
                            </Typography>
                        )}
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                        <TextField
                            size="small"
                            placeholder="Search by name, roll no, email..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            sx={{ minWidth: "280px" }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Box>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadClick}
                            >
                                Download
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleCloseMenu}
                            >
                                <MenuItem onClick={() => handleDownload("csv")}>
                                    Download CSV
                                </MenuItem>
                                <MenuItem onClick={() => handleDownload("pdf")}>
                                    Download PDF
                                </MenuItem>
                            </Menu>
                        </Box>
                        <Tooltip title="Close">
                            <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer
                            component={Paper}
                            sx={{
                                maxHeight: "calc(90vh - 150px)",
                                overflow: "auto",
                            }}
                        >
                            <Table stickyHeader sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }}>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>Roll No</StyledTableCell>
                                        <StyledTableCell>Name</StyledTableCell>
                                        <StyledTableCell>Institution Email</StyledTableCell>
                                        <StyledTableCell>Personal Gmail</StyledTableCell>
                                        <StyledTableCell>Mobile</StyledTableCell>
                                        <StyledTableCell>In Time</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedModules.length > 0 ? (
                                        paginatedModules.map((learner) => (
                                            <StyledTableRow key={learner?.id}>
                                                <StyledTableCell>{highlightText(learner?.roll_num, search)}</StyledTableCell>
                                                <StyledTableCell>{highlightText(learner?.name, search)}</StyledTableCell>
                                                <StyledTableCell sx={{ textTransform: "none !important" }}>{highlightText(learner?.institution_email, search)}</StyledTableCell>
                                                <StyledTableCell sx={{ textTransform: "none !important" }}>{highlightText(learner?.email, search)}</StyledTableCell>
                                                <StyledTableCell>{highlightText(learner?.mobile, search)}</StyledTableCell>
                                                <StyledTableCell>   {learner?.inDateTime ? learner.inDateTime.split(" ").slice(1).join(" ") : "-"}
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))
                                    ) : (
                                        <StyledTableRow>
                                            <StyledTableCell colSpan={6} align="center">No records found</StyledTableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[10, 20, 30, 60, 100]}
                            component="div"
                            count={filteredLearners.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </>
                )}
            </Box>
        </Modal>
    );
}

export default AttendanceDetailsModal;